import { forwardRef } from 'react';

import type {
  AccessibilityRole,
  TextProps as RNTextProps,
  StyleProp,
  TextProps,
  TextStyle,
} from 'react-native';
import { Text as RNText, StyleSheet } from 'react-native';

import { primitive } from 'styles/constants';
import { HeaderAccessibilityLevel, fontConfig, fontSizes } from 'styles/constants/fonts';
import { textVariants } from 'styles/constants/fonts';

import type { SpacingProps } from '../../utils/element-spacings';
import { getStylesWithSpacing } from '../../utils/element-spacings';

export interface TypographyProps extends Omit<RNTextProps, 'style'>, SpacingProps {
  variant?: TextVariant;
  style?: StyleProp<TextStyle>;
  children?: React.ReactNode;
  color?: string;
  bold?: boolean;
  fontFamily?: 'heading' | 'body';
  fontSize?: number | FontSizeToken;
  textAlign?: TextStyle['textAlign'];
}
/**
 * IN PROGRESS: Drop in NativeBase replacement.
 * A styled Text component using React Native's Text with design system variants.
 *
 * @example
 * ```tsx
 * <StyledText variant="headerOne" color="green" bold>
 *   Bold Green Header Text with correct accessibilityRole
 * </StyledText>
 * ```
 */
export const Typography = forwardRef<RNText, TypographyProps>(
  (
    { variant = 'copyOne', style, color, bold, fontSize, textAlign, fontFamily, ...restProps },
    ref
  ) => {
    // map convenience properties to real styles
    const boldStyle = bold
      ? fontFamilyMap[textVariants[variant]?.fontFamily ?? 'body']?.bold
      : undefined;

    // final calculated style, passed in style wins
    const finalStyle = getStylesWithSpacing(
      [
        stylesheetStyles.base,
        stylesheetStyles[variant],
        boldStyle,
        fontSize
          ? typeof fontSize === 'number'
            ? { fontSize }
            : stylesheetStyles[fontSize as TextVariant]
          : undefined,
        fontFamily ? fontFamilyMap[fontFamily][bold ? 'bold' : 'regular'] : undefined,
        color ? { color } : undefined,
        textAlign ? { textAlign } : undefined,
      ],
      style,
      restProps
    );

    const finalProps = addVariantProps(variant, restProps);

    return (
      <RNText ref={ref} style={finalStyle} {...finalProps}>
        {restProps.children}
      </RNText>
    );
  }
);
Typography.displayName = 'Typography';

type TextVariant = keyof typeof textVariants;
type FontSizeToken = keyof typeof fontSizes;

export const fontHeadingStyles = StyleSheet.create({
  regular: { fontFamily: fontConfig.Flame[400].normal },
  bold: { fontFamily: fontConfig.Flame[700].normal },
});

export const fontBodyStyles = StyleSheet.create({
  regular: { fontFamily: fontConfig['Flame-Sans'][400].normal },
  bold: { fontFamily: fontConfig['Flame-Sans'][700].normal },
});

const fontFamilyMap = {
  heading: fontHeadingStyles,
  body: fontBodyStyles,
};

// TODO refactor this to be static styles after nativebase Text removal
const stylesheetStyles = StyleSheet.create({
  base: {
    color: primitive.$bbqBrown,
    fontFamily: fontConfig['Flame-Sans'][400].normal,
  },
  ...Object.keys(textVariants).reduce((acc, key: TextVariant) => {
    const config = textVariants[key];
    const style: TextStyle = {
      fontSize: getMobileValue(config.fontSize),
    };

    if ('lineHeight' in config) {
      style.lineHeight = getMobileValue(config.lineHeight);
    }

    if (config.fontFamily) {
      style.fontFamily = fontFamilyMap[config.fontFamily].regular.fontFamily;
    }
    if ('textDecorationLine' in config) {
      style.textDecorationLine = config.textDecorationLine as TextStyle['textDecorationLine'];
    }

    // TODO refactor this to real styles after nativebase Text removal
    if ('mb' in config) {
      style.marginBottom = config.mb * 4;
    }
    if ('cursor' in config) {
      style.cursor = config.cursor as TextStyle['cursor'];
    }
    acc[key] = style;
    return acc;
  }, {} as Record<TextVariant, TextStyle>),
  ...Object.keys(fontSizes).reduce((acc, key: FontSizeToken) => {
    acc[key] = { fontSize: fontSizes[key] };
    return acc;
  }, {} as Record<TextVariant, TextStyle>),
});

function getMobileValue(value: number | number[]): number {
  return Array.isArray(value) ? value[0] : value;
}

// TODO refactor this with nativebase <Text/> removal
function addVariantProps(variant: TextVariant, incomingProps: TextProps): TextProps {
  const variantConfig = textVariants[variant];

  if ('accessibilityRole' in variantConfig && !incomingProps.accessibilityRole) {
    incomingProps.accessibilityRole = variantConfig.accessibilityRole as AccessibilityRole;
  }
  if ('accessibilityLevel' in variantConfig && !incomingProps.accessibilityLevel) {
    incomingProps.accessibilityLevel = variantConfig.accessibilityLevel as HeaderAccessibilityLevel;
  }
  if ('maxFontSizeMultiplier' in variantConfig && !incomingProps.maxFontSizeMultiplier) {
    incomingProps.maxFontSizeMultiplier = variantConfig.maxFontSizeMultiplier;
  }
  return incomingProps;
}
